import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { buildListingRequestParams } from "@/utils/directoryQuery";
import { devError } from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function loadSearchRecovery(query) {
  try {
    const response = await axios.get(`${API}/search/recovery`, { params: { q: query } });
    return response.data || null;
  } catch (error) {
    devError("Search recovery could not be loaded:", error);
    return null;
  }
}

export function useDirectoryListings({ queryFilters, searchQuery, showFavsOnly }) {
  const [items, setItems] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [searchRecovery, setSearchRecovery] = useState(null);
  const pageRef = useRef(1);
  const requestRef = useRef(0);
  const buildParams = useCallback(
    (pageNum) => buildListingRequestParams(pageNum, queryFilters),
    [queryFilters],
  );

  const beginRequest = (append) => {
    if (append) setLoadingMore(true);
    else setRefreshing(true);
  };

  const finishRequest = (requestId) => {
    if (requestId !== requestRef.current) return;
    setLoading(false);
    setLoadingMore(false);
    setRefreshing(false);
  };

  const applyResponse = useCallback((data, pageNum, append, recovery) => {
    const newItems = data.items || [];
    setItems((previous) => append ? [...previous, ...newItems] : newItems);
    setTotal(data.total ?? newItems.length);
    setHasMore(showFavsOnly ? false : Boolean(data.hasMore));
    if (pageNum === 1) setFeatured(data.featured || []);
    setSearchRecovery(recovery);
    pageRef.current = pageNum;
    setError("");
  }, [showFavsOnly]);

  const runFetch = useCallback(async (pageNum, append) => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    beginRequest(append);
    try {
      const { data } = await axios.get(`${API}/listings`, { params: buildParams(pageNum) });
      if (requestId !== requestRef.current) return;
      const needsRecovery = pageNum === 1 && !append && (data.total || 0) === 0 && searchQuery.trim().length >= 2;
      const recovery = needsRecovery ? await loadSearchRecovery(searchQuery.trim()) : null;
      if (requestId !== requestRef.current) return;
      applyResponse(data, pageNum, append, recovery);
    } catch (requestError) {
      devError("Directory listings could not be loaded:", requestError);
      if (requestId === requestRef.current) setError("Unable to load listings. Please try again.");
    } finally {
      finishRequest(requestId);
    }
  }, [applyResponse, buildParams, searchQuery]);

  useEffect(() => { runFetch(1, false); }, [runFetch]);
  const loadNextPage = useCallback(() => runFetch(pageRef.current + 1, true), [runFetch]);
  return { error, featured, hasMore, items, loadNextPage, loading, loadingMore, refreshing, searchRecovery, total };
}